import { LS_GEO_COORDS } from './constants';

// Kept free of store/api imports: getUserLocation reads it on the login path.
const DEV_BUILD_MODES = ['local-dev', 'development'];

export type GeoCoordsOverride = { latitude: number; longitude: number; presetKey: string | null };

export const isDebugBuild = (): boolean => DEV_BUILD_MODES.includes(import.meta.env.MODE);

const isInRange = (value: unknown, maxAbs: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= maxAbs;

export const isValidGeoCoords = (latitude: unknown, longitude: unknown): boolean =>
  isInRange(latitude, 90) && isInRange(longitude, 180);

export function parseGeoCoordsFields(
  latitude: string,
  longitude: string,
): { latitude: number; longitude: number } | null {
  if (!latitude.trim() || !longitude.trim()) {
    return null;
  }
  const coords = { latitude: Number(latitude), longitude: Number(longitude) };
  return isValidGeoCoords(coords.latitude, coords.longitude) ? coords : null;
}

// Accepts "lat, lng" as copied from Google Maps (comma and/or whitespace separated).
export function parseGeoCoordsInput(text: string): { latitude: number; longitude: number } | null {
  const parts = text.split(/[\s,]+/).filter(Boolean);
  return parts.length === 2 ? parseGeoCoordsFields(parts[0], parts[1]) : null;
}

// Dev builds only (fail-closed): a stale value left in localStorage never reaches production.
export function readGeoCoordsOverride(): GeoCoordsOverride | null {
  if (!isDebugBuild()) {
    return null;
  }
  try {
    const raw = localStorage.getItem(LS_GEO_COORDS);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<GeoCoordsOverride>;
    if (!isValidGeoCoords(parsed.latitude, parsed.longitude)) {
      return null;
    }
    return {
      latitude: parsed.latitude as number,
      longitude: parsed.longitude as number,
      presetKey: typeof parsed.presetKey === 'string' ? parsed.presetKey : null,
    };
  } catch {
    return null;
  }
}

export function writeGeoCoordsOverride(value: GeoCoordsOverride | null): void {
  if (value) {
    localStorage.setItem(LS_GEO_COORDS, JSON.stringify(value));
  } else {
    localStorage.removeItem(LS_GEO_COORDS);
  }
}
